import React, { useEffect, useState } from "react";
import { Layout } from "../components/layout/Layout";
import { useLanguage } from "../contexts/LanguageContext";
import {
  getQuotations,
  createQuotation,
  updateQuotation,
  convertQuotationToInvoice,
  getClients,
  getProjects,
  getCompanyInfo,
  getServices,
} from "../lib/api";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ActionButton,
  AppDialog,
  DataTable,
  PageToolbar,
  StatusBadge,
} from "../components/app";
import {
  QuotationCreateForm,
  QuotationEditForm,
  QuotationPreviewModal,
  ServiceCatalogDialog,
  createQuotationColumns,
} from "../components/quotations";
import html2pdf from "html2pdf.js";
import { Plus, MagnifyingGlass, FileText, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const formatMoney = (value) => Number(value || 0).toFixed(2);
const QUOTATION_NUMBER_PATTERN = /^COT-\d{6}-\d{4}$/;
const PAGE_SIZE = 20;

const getTodayDateInput = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};


export function QuotationsPage() {
  const { t } = useLanguage();
  const [quotations, setQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [services, setServices] = useState([]);
  const [showServiceSelector, setShowServiceSelector] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [viewingQuotation, setViewingQuotation] = useState(null);
  const [internalNotesOpen, setInternalNotesOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_id: "",
    project_id: "",
    items: [{ description: "", quantity: 1, unit_price: 0, total: 0 }],
    subtotal: 0,
    itbms_rate: 7.0,
    itbms_amount: 0,
    total: 0,
    validity_days: 30,
    use_custom_quotation_number: false,
    quotation_number: "",
    use_custom_issue_date: false,
    issue_date: getTodayDateInput(),
    payment_terms: "50% anticipo, 50% contra entrega",
    delivery_time: "",
    notes: "",
    internal_notes: "",
    warranty: "",
  });
  const [editFormData, setEditFormData] = useState({
    client_id: "",
    project_id: "",
    items: [],
    subtotal: 0,
    itbms_rate: 7.0,
    itbms_amount: 0,
    total: 0,
    validity_days: 30,
    use_custom_quotation_number: false,
    quotation_number: "",
    use_custom_issue_date: false,
    issue_date: "",
    payment_terms: "",
    delivery_time: "",
    notes: "",
    internal_notes: "",
    warranty: "",
    status: "",
  });

  const STATUS_LABELS = {
    borrador: t("statusDraft"),
    enviada: t("statusSent"),
    aceptada: t("statusAccepted"),
    rechazada: t("statusRejected"),
    vencida: t("statusExpired"),
    facturada: t("statusInvoiced"),
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, monthFilter, searchTerm, showAllRecords, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [monthFilter, searchTerm, showAllRecords, statusFilter]);

  useEffect(() => {
    setInternalNotesOpen(false);
  }, [viewingQuotation?._id]);

  const normalizeCollection = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const getEntityId = (entity) =>
    String(entity?._id || entity?.id || entity?.client_id || "");

  const getEntityName = (entity) =>
    entity?.name || entity?.company || entity?.contact_name || "Sin nombre";

  const getEntityTaxId = (entity) =>
    entity?.tax_id || entity?.ruc || entity?.taxId || "";

  const formatClientDisplay = (client) => {
    if (!client) return "-";
    const name = getEntityName(client);
    const taxId = getEntityTaxId(client);
    return taxId ? `${name} - RUC: ${taxId}` : name;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (monthFilter) params.month = monthFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (showAllRecords) {
        params.limit = 0;
      } else {
        params.skip = (currentPage - 1) * PAGE_SIZE;
        params.limit = PAGE_SIZE + 1;
      }

      const [quotationsRes, clientsRes, projectsRes, companyRes, servicesRes] =
        await Promise.all([
          getQuotations(params).catch(() => ({ data: [] })),
          getClients().catch(() => ({ data: [] })),
          getProjects().catch(() => ({ data: [] })),
          getCompanyInfo().catch(() => ({ data: null })),
          getServices({ is_active: true }).catch(() => ({ data: [] })),
        ]);

      const quotationItems = normalizeCollection(quotationsRes.data);
      setHasNextPage(!showAllRecords && quotationItems.length > PAGE_SIZE);
      setQuotations(
        showAllRecords ? quotationItems : quotationItems.slice(0, PAGE_SIZE),
      );
      setClients(normalizeCollection(clientsRes.data));
      setProjects(normalizeCollection(projectsRes.data));
      setCompanyInfo(companyRes.data);
      setServices(normalizeCollection(servicesRes.data));
    } catch (error) {
      toast.error(t("errorOccurred"));
    } finally {
      setLoading(false);
    }
  };

  const addServiceFromCatalog = (service) => {
    const newItem = {
      description:
        service.name + (service.description ? ` - ${service.description}` : ""),
      quantity: 1,
      unit_price: service.sale_price,
      total: service.sale_price,
    };

    const newItems = [...formData.items, newItem];
    const totals = calculateTotals(newItems, formData.itbms_rate);

    setFormData({
      ...formData,
      items: newItems,
      ...totals,
    });

    setShowServiceSelector(false);
    toast.success("Servicio agregado");
  };

  const calculateTotals = (items, itbmsRate) => {
    const subtotal = items.reduce(
      (sum, item) => sum + (Number(item.total) || 0),
      0,
    );
    const itbms_amount = subtotal * (Number(itbmsRate) / 100);
    return { subtotal, itbms_amount, total: subtotal + itbms_amount };
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };

    if (field === "quantity" || field === "unit_price") {
      newItems[index].total =
        (Number(newItems[index].quantity) || 0) *
        (Number(newItems[index].unit_price) || 0);
    }

    const totals = calculateTotals(newItems, formData.itbms_rate);
    setFormData({ ...formData, items: newItems, ...totals });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        { description: "", quantity: 1, unit_price: 0, total: 0 },
      ],
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    const totals = calculateTotals(newItems, formData.itbms_rate);
    setFormData({ ...formData, items: newItems, ...totals });
  };

  const handleItbmsChange = (rate) => {
    const totals = calculateTotals(formData.items, rate);
    setFormData({ ...formData, itbms_rate: rate, ...totals });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.client_id) {
      toast.error(t("selectClient"));
      return;
    }
    if (formData.use_custom_issue_date && !formData.issue_date) {
      toast.error("Selecciona la fecha del documento.");
      return;
    }
    if (
      formData.use_custom_quotation_number &&
      !formData.quotation_number.trim()
    ) {
      toast.error("Ingresa el número de cotización.");
      return;
    }
    if (
      formData.use_custom_quotation_number &&
      !QUOTATION_NUMBER_PATTERN.test(formData.quotation_number.trim())
    ) {
      toast.error("El número debe usar el formato COT-YYYYMM-0000.");
      return;
    }

    try {
      const { use_custom_issue_date, use_custom_quotation_number, ...payload } =
        formData;
      if (!use_custom_issue_date) {
        delete payload.issue_date;
      }
      if (!use_custom_quotation_number) {
        delete payload.quotation_number;
      } else {
        payload.quotation_number = payload.quotation_number.trim();
      }
      await createQuotation(payload);
      toast.success(t("quotationCreated"));
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("errorOccurred"));
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateQuotation(id, { status });
      toast.success(t("successSaved"));
      fetchData();
      setViewingQuotation(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("errorOccurred"));
    }
  };

  // Open edit dialog for an existing quotation
  const openEditDialog = (quotation) => {
    setEditingQuotation(quotation);
    setEditFormData({
      client_id: quotation.client_id || "",
      project_id: quotation.project_id || "",
      items: (quotation.items || []).map((item) => ({ ...item })),
      subtotal: quotation.subtotal || 0,
      itbms_rate: quotation.itbms_rate || 7.0,
      itbms_amount: quotation.itbms_amount || 0,
      total: quotation.total || 0,
      validity_days: quotation.validity_days || 30,
      use_custom_quotation_number: false,
      quotation_number: quotation.quotation_number || "",
      use_custom_issue_date: Boolean(quotation.issue_date_overridden),
      issue_date: quotation.issue_date || getTodayDateInput(),
      payment_terms: quotation.payment_terms || "",
      delivery_time: quotation.delivery_time || "",
      notes: quotation.notes || "",
      internal_notes: quotation.internal_notes || "",
      warranty: quotation.warranty || "",
      status: quotation.status || "",
    });
    setEditDialogOpen(true);
  };

  // Content can be edited while a quotation is still internal or has only been sent.
  const canEditQuotationContent = ["borrador", "enviada"].includes(
    editingQuotation?.status,
  );

  // Edit mode: update an item
  const updateEditItem = (index, field, value) => {
    const newItems = [...editFormData.items];
    newItems[index][field] = value;

    if (field === "quantity" || field === "unit_price") {
      newItems[index].total =
        (newItems[index].quantity || 0) * (newItems[index].unit_price || 0);
    }

    const totals = calculateTotals(newItems, editFormData.itbms_rate);
    setEditFormData({ ...editFormData, items: newItems, ...totals });
  };

  // Edit mode: add/remove items
  const addEditItem = () => {
    setEditFormData({
      ...editFormData,
      items: [
        ...editFormData.items,
        { description: "", quantity: 1, unit_price: 0, total: 0 },
      ],
    });
  };

  const removeEditItem = (index) => {
    const newItems = editFormData.items.filter((_, i) => i !== index);
    const totals = calculateTotals(newItems, editFormData.itbms_rate);
    setEditFormData({ ...editFormData, items: newItems, ...totals });
  };

  // Edit mode: ITBMS rate change
  const handleEditItbmsChange = (rate) => {
    const totals = calculateTotals(editFormData.items, rate);
    setEditFormData({ ...editFormData, itbms_rate: rate, ...totals });
  };

  // Handle edit form submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingQuotation) return;
    if (editFormData.use_custom_issue_date && !editFormData.issue_date) {
      toast.error("Selecciona la fecha del documento.");
      return;
    }
    if (
      editFormData.use_custom_quotation_number &&
      !editFormData.quotation_number.trim()
    ) {
      toast.error("Ingresa el número de cotización.");
      return;
    }
    if (
      editFormData.use_custom_quotation_number &&
      !QUOTATION_NUMBER_PATTERN.test(editFormData.quotation_number.trim())
    ) {
      toast.error("El número debe usar el formato COT-YYYYMM-0000.");
      return;
    }

    const updatePayload = {};

    // Always-editable fields
    if (editFormData.status !== editingQuotation.status)
      updatePayload.status = editFormData.status;
    if (editFormData.notes !== (editingQuotation.notes || ""))
      updatePayload.notes = editFormData.notes;
    if (editFormData.internal_notes !== (editingQuotation.internal_notes || ""))
      updatePayload.internal_notes = editFormData.internal_notes;

    // Content fields can be edited before the quotation is accepted, rejected, expired, or invoiced.
    if (canEditQuotationContent) {
      if (editFormData.client_id !== editingQuotation.client_id)
        updatePayload.client_id = editFormData.client_id;
      if (editFormData.project_id !== (editingQuotation.project_id || ""))
        updatePayload.project_id = editFormData.project_id;
      if (editFormData.itbms_rate !== (editingQuotation.itbms_rate || 0))
        updatePayload.itbms_rate = editFormData.itbms_rate;
      if (editFormData.subtotal !== (editingQuotation.subtotal || 0))
        updatePayload.subtotal = editFormData.subtotal;
      if (editFormData.itbms_amount !== (editingQuotation.itbms_amount || 0))
        updatePayload.itbms_amount = editFormData.itbms_amount;
      if (editFormData.total !== (editingQuotation.total || 0))
        updatePayload.total = editFormData.total;
      if (editFormData.validity_days !== (editingQuotation.validity_days || 0))
        updatePayload.validity_days = editFormData.validity_days;
      if (editFormData.use_custom_quotation_number)
        updatePayload.quotation_number = editFormData.quotation_number.trim();
      if (editFormData.use_custom_issue_date)
        updatePayload.issue_date = editFormData.issue_date;
      if (editFormData.payment_terms !== (editingQuotation.payment_terms || ""))
        updatePayload.payment_terms = editFormData.payment_terms;
      if (editFormData.delivery_time !== (editingQuotation.delivery_time || ""))
        updatePayload.delivery_time = editFormData.delivery_time;
      if (editFormData.warranty !== (editingQuotation.warranty || ""))
        updatePayload.warranty = editFormData.warranty;
      if (
        JSON.stringify(editFormData.items) !==
        JSON.stringify(editingQuotation.items || [])
      )
        updatePayload.items = editFormData.items;
    }

    if (Object.keys(updatePayload).length === 0) {
      toast.info("No changes detected");
      return;
    }

    try {
      await updateQuotation(editingQuotation._id, updatePayload);
      toast.success(t("successSaved"));
      setEditDialogOpen(false);
      setEditingQuotation(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("errorOccurred"));
    }
  };

  const handleConvertToInvoice = async (id) => {
    try {
      await convertQuotationToInvoice(id);
      toast.success(t("invoiceCreated"));
      fetchData();
      setViewingQuotation(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("errorOccurred"));
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      project_id: "",
      items: [{ description: "", quantity: 1, unit_price: 0, total: 0 }],
      subtotal: 0,
      itbms_rate: 7.0,
      itbms_amount: 0,
      total: 0,
      validity_days: 30,
      use_custom_quotation_number: false,
      quotation_number: "",
      use_custom_issue_date: false,
      issue_date: getTodayDateInput(),
      payment_terms: "50% anticipo, 50% contra entrega",
      delivery_time: "",
      notes: "",
      internal_notes: "",
      warranty: "",
    });
  };

  const getClientName = (clientId) => {
    const client = clients.find(
      (c) => getEntityId(c) === String(clientId || ""),
    );
    return formatClientDisplay(client);
  };

  const printQuotation = () => window.print();

  const exportPdf = async () => {
    try {
      const element = document.getElementById("quotation-pdf");

      if (!element) {
        toast.error("No se encontró el contenido para exportar.");
        return;
      }

      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      await new Promise((resolve) => setTimeout(resolve, 150));

      const opt = {
        margin: 0,
        filename: `${viewingQuotation?.quotation_number || "Cotizacion"}.pdf`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          scale: 3,
          useCORS: true,
          backgroundColor: "#ffffff",
          scrollX: 0,
          scrollY: 0,
          letterRendering: true,
        },
        jsPDF: {
          unit: "pt",
          format: "a4",
          orientation: "portrait",
        },
        pagebreak: {
          mode: ["avoid-all", "css", "legacy"],
        },
      };

      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error(error);
      toast.error("No se pudo generar el PDF.");
    }
  };

  const selectedQuotationClientName = viewingQuotation
    ? formatClientDisplay(viewingQuotation.client) !== "-"
      ? formatClientDisplay(viewingQuotation.client)
      : getClientName(viewingQuotation.client_id)
    : "-";

  const quotationColumns = createQuotationColumns({
    t,
    formatMoney,
    getClientName,
    onViewQuotation: setViewingQuotation,
    statusLabels: STATUS_LABELS,
  });

  return (
    <Layout title={t("quotations")}>
      <div className="space-y-6" data-testid="quotations-page">
        <PageToolbar
          filters={
            <>
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                placeholder="Buscar por cotización, descripción o monto"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 valhalla-input"
                data-testid="quotation-search"
              />
            </div>

            <Input
              type="month"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="w-full valhalla-input sm:w-40"
              aria-label="Filtrar cotizaciones por mes"
            />

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 valhalla-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{t("all")}</SelectItem>
                <SelectItem value="borrador">{t("statusDraft")}</SelectItem>
                <SelectItem value="enviada">{t("statusSent")}</SelectItem>
                <SelectItem value="aceptada">{t("statusAccepted")}</SelectItem>
                <SelectItem value="rechazada">{t("statusRejected")}</SelectItem>
                <SelectItem value="facturada">{t("statusInvoiced")}</SelectItem>
              </SelectContent>
            </Select>
            <ActionButton
              label={showAllRecords ? "20 por página" : "Ver todo"}
              tone={showAllRecords ? "warning" : "info"}
              onClick={() => setShowAllRecords((value) => !value)}
            />
            </>
          }
          actions={
            <AppDialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (!open) resetForm();
              }}
              size="xl"
              title={t("newQuotation")}
              trigger={
                <ActionButton
                  icon={Plus}
                  label={t("newQuotation")}
                  tone="primary"
                  className="font-semibold"
                  data-testid="new-quotation-btn"
                />
              }
            >
              <QuotationCreateForm
                formData={formData}
                setFormData={setFormData}
                clients={clients}
                projects={projects}
                getEntityId={getEntityId}
                formatClientDisplay={formatClientDisplay}
                getTodayDateInput={getTodayDateInput}
                t={t}
                formatMoney={formatMoney}
                onSubmit={handleSubmit}
                onCancel={() => setIsDialogOpen(false)}
                onAddItem={addItem}
                onUpdateItem={updateItem}
                onRemoveItem={removeItem}
                onItbmsChange={handleItbmsChange}
                onOpenCatalog={() => setShowServiceSelector(true)}
              />
            </AppDialog>
          }
        />
        <ServiceCatalogDialog
          open={showServiceSelector}
          onOpenChange={setShowServiceSelector}
          services={services}
          onAddService={addServiceFromCatalog}
        />

        {/* Edit Quotation Dialog */}
        <Dialog
          open={editDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              setEditDialogOpen(false);
              setEditingQuotation(null);
            }
          }}
        >
          <DialogContent className="bg-[#151B26] border-[#242E42] max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#F3F4F6] font-['Outfit']">
                {t("editQuotation")}: {editingQuotation?.quotation_number}
              </DialogTitle>
            </DialogHeader>

            <QuotationEditForm
              editingQuotation={editingQuotation}
              formData={editFormData}
              setFormData={setEditFormData}
              clients={clients}
              projects={projects}
              getEntityId={getEntityId}
              formatClientDisplay={formatClientDisplay}
              getTodayDateInput={getTodayDateInput}
              t={t}
              statusLabels={STATUS_LABELS}
              canEditContent={canEditQuotationContent}
              formatMoney={formatMoney}
              onSubmit={handleEditSubmit}
              onCancel={() => {
                setEditDialogOpen(false);
                setEditingQuotation(null);
              }}
              onAddItem={addEditItem}
              onUpdateItem={updateEditItem}
              onRemoveItem={removeEditItem}
              onItbmsChange={handleEditItbmsChange}
            />
          </DialogContent>
        </Dialog>

        <DataTable
          columns={quotationColumns}
          data={quotations}
          getRowKey={(quotation) => quotation._id}
          loading={loading}
          loadingLabel={t("loading")}
          emptyIcon={FileText}
          emptyDescription={t("noQuotations")}
          mobileTitle={(quotation) => quotation.quotation_number}
          mobileSubtitle={(quotation) => getClientName(quotation.client_id)}
          mobileBadge={(quotation) => (
            <StatusBadge
              value={quotation.status}
              label={STATUS_LABELS[quotation.status]}
              statusContext="quotation"
            />
          )}
          mobileActions={(quotation) => (
            <ActionButton
              icon={Eye}
              label="Ver"
              tone="view"
              onClick={() => setViewingQuotation(quotation)}
              data-testid={`view-quotation-${quotation._id}`}
            />
          )}
        />

        {!showAllRecords ? (
          <div className="flex flex-col gap-3 rounded-lg border border-[#242E42] bg-[#151B26] p-3 text-sm text-[#9CA3AF] sm:flex-row sm:items-center sm:justify-between">
            <span>
              Mostrando hasta {PAGE_SIZE} cotizaciones por página · Página {currentPage}
            </span>
            <div className="flex items-center gap-2">
              <ActionButton
                label="Anterior"
                tone="default"
                disabled={currentPage === 1 || loading}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              />
              <ActionButton
                label="Siguiente"
                tone="default"
                disabled={loading || !hasNextPage}
                onClick={() => setCurrentPage((page) => page + 1)}
              />
            </div>
          </div>
        ) : null}

        <QuotationPreviewModal
          quotation={viewingQuotation}
          open={!!viewingQuotation}
          onOpenChange={() => setViewingQuotation(null)}
          t={t}
          companyInfo={companyInfo}
          clientName={selectedQuotationClientName}
          statusLabel={
            viewingQuotation ? STATUS_LABELS[viewingQuotation.status] : undefined
          }
          internalNotesOpen={internalNotesOpen}
          onToggleInternalNotes={() =>
            setInternalNotesOpen((isOpen) => !isOpen)
          }
          onClose={() => setViewingQuotation(null)}
          onPrint={printQuotation}
          onExportPdf={exportPdf}
          onEdit={() => {
            if (!viewingQuotation) return;
            setViewingQuotation(null);
            openEditDialog(viewingQuotation);
          }}
          onMarkSent={() =>
            viewingQuotation &&
            handleStatusChange(viewingQuotation._id, "enviada")
          }
          onAccept={() =>
            viewingQuotation &&
            handleStatusChange(viewingQuotation._id, "aceptada")
          }
          onReject={() =>
            viewingQuotation &&
            handleStatusChange(viewingQuotation._id, "rechazada")
          }
          onConvertToInvoice={() =>
            viewingQuotation && handleConvertToInvoice(viewingQuotation._id)
          }
        />
      </div>
    </Layout>
  );
}

export default QuotationsPage;
